import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { categories, fallbackProjects, adaptProjects } from "@/data/projectsAdapter";
import VideoModal from "@/components/VideoModal";
import { ArrowUpRight, PlayCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback set — used only if no BTS videos exist in the CMS yet.
const FALLBACK_BTS = [
  { src: "/covers/kxy.jpg", video: "https://www.youtube.com/watch?v=kXyOLZgougk" },
  { src: "/covers/qvx.jpg", video: "https://www.youtube.com/watch?v=QVxZrQoG0Uk" },
  { src: "/covers/hjt.jpg", video: "https://www.youtube.com/watch?v=hJttvSNTdYY" },
  { src: "/covers/wo2.jpg", video: "https://www.youtube.com/watch?v=Wo2_hrXQjYY" },
];

const Work = () => {
  const { t, lang } = useLang();
  const [active, setActive] = useState("All");
  const [projects, setProjects] = useState(fallbackProjects);
  const [activeBtsVideo, setActiveBtsVideo] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/portfolio/projects`)
      .then(({ data }) => {
        if (!mounted) return;
        const adapted = adaptProjects(data);
        if (adapted.length > 0) setProjects(adapted);
      })
      .catch(() => { /* keep fallback */ });
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    if (active === "All") return projects;
    return projects.filter((p) => p.category === active);
  }, [active, projects]);

  // ALL Behind-the-Scenes items with a playable video — sorted by order/created.
  // The section grows automatically as more BTS videos are added from /admin/portfolio.
  const btsTiles = useMemo(() => {
    const bts = projects
      .filter((p) => p.category === "Behind the Scenes" && p.video)
      .map((p) => ({ src: p.image || "/covers/basma-tech.jpg", video: p.video, title: p.title?.[lang] || "" }));
    return bts.length > 0 ? bts : FALLBACK_BTS.map((x) => ({ ...x, title: "" }));
  }, [projects, lang]);

  const showComingSoon = active === "Documentary" && filtered.length === 0;

  return (
    <div data-testid="work-page">
      <section className="pt-24 pb-10 md:pt-28 md:pb-16">
        <div className="container-x max-w-5xl">
          <div className="overline mb-6">{t("work.eyebrow")}</div>
          <h1 className="display-h1">{t("work.title")}</h1>
          <p className="mt-8 text-white/70 text-xl leading-relaxed max-w-3xl">{t("work.lead")}</p>
        </div>
      </section>

      <section className="pb-6">
        <div className="container-x">
          <div className="flex items-center gap-3 flex-wrap" data-testid="work-filters">
            <span className="overline me-2">{t("work.filterLabel")}</span>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`filter-${c.replace(/\s|&/g, "").toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                  active === c
                    ? "bg-[#E6A95B] text-[#0A0A0C] border-[#E6A95B]"
                    : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
                }`}
              >
                {t(`cats.${c}`)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="container-x">
          <AnimatePresence mode="popLayout">
            {showComingSoon ? (
              <motion.div
                key="coming-soon"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="documentary-coming-soon"
                className="relative rounded-3xl overflow-hidden border border-white/10 min-h-[420px] md:min-h-[520px] grid place-items-center"
              >
                <img
                  src="https://images.unsplash.com/photo-1489599735734-79b4212bea1e?w=1800&q=80"
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/70 via-[#0A0A0C]/60 to-[#0A0A0C]/90" />
                <div className="relative text-center px-6 max-w-2xl">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E6A95B]/40 bg-black/40 backdrop-blur text-[#E6A95B] text-xs uppercase tracking-[0.28em] mb-6">
                    <Clock className="w-4 h-4" /> {t(`cats.${active}`)}
                  </div>
                  <h2 className="display-h1 italic">{t("work.comingSoonTitle")}</h2>
                  <p className="mt-6 text-white/70 text-lg md:text-xl leading-relaxed">
                    {t("work.comingSoonSub")}
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-8"
              >
                {filtered.map((p) => {
                  const isExternal = !!p.externalUrl;
                  const href = isExternal ? p.externalUrl : `/work/${p.slug}`;
                  return (
                    <a
                      key={p.id}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`work-card-${p.slug}`}
                      className="group"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-white/5">
                        <img src={p.image} alt={p.title[lang]} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[1200ms] group-hover:scale-110" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/70 via-transparent to-transparent" />
                        <div className="absolute top-4 start-4 flex gap-2">
                          <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.24em] bg-black/50 backdrop-blur border border-white/10 text-[#E6A95B]">
                            {t(`cats.${p.category}`)}
                          </span>
                          <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.24em] bg-black/50 backdrop-blur border border-white/10 text-white/70">
                            {p.year}
                          </span>
                        </div>
                      </div>
                      <div className="mt-6 flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-display text-2xl md:text-3xl leading-tight">{p.title[lang]}</h3>
                          <p className="mt-2 text-white/60 max-w-md">{p.short[lang]}</p>
                        </div>
                        <div className="w-11 h-11 shrink-0 grid place-items-center rounded-full border border-white/15 text-white group-hover:bg-[#E6A95B] group-hover:text-[#0A0A0C] group-hover:border-[#E6A95B] transition">
                          <ArrowUpRight className="w-4 h-4" />
                        </div>
                      </div>
                    </a>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Methodology teaser (linked to /methodology) */}
      <section className="py-24 md:py-32 bg-[#0d0d10] border-t border-white/5" data-testid="bts-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center rtl:[direction:ltr]">
          <div className="lg:col-span-5 rtl:[direction:rtl]">
            <div className="overline mb-4">{t("work.workBtsEyebrow")}</div>
            <a
              href="/methodology"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="methodology-link"
              className="group inline-block"
            >
              <h2 className="display-h2 group-hover:text-[#E6A95B] transition-colors">
                {t("work.workBtsTitle")}
              </h2>
            </a>
            <p className="mt-6 text-white/60 text-lg" dangerouslySetInnerHTML={{ __html: t("work.workBtsText") }} />
            <div className="mt-8">
              <a
                href="/methodology"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="methodology-cta-link"
                className="btn-primary inline-flex items-center gap-2"
              >
                {t("work.workBtsCta")} <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            {btsTiles.map((item, i) => {
              const aspect = i % 3 === 0 ? "aspect-[4/5]" : "aspect-square";
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveBtsVideo(item.video)}
                  data-testid={`bts-image-${i}`}
                  className={`rounded-xl overflow-hidden border border-white/5 relative group ${aspect} bg-[#0d0d10]`}
                >
                  <img src={item.src} alt={item.title || ""} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 grid place-items-center bg-black/0 group-hover:bg-black/40 transition">
                    <div className="w-16 h-16 rounded-full bg-[#E6A95B] text-[#0A0A0C] grid place-items-center opacity-90 group-hover:opacity-100 group-hover:scale-110 shadow-[0_10px_40px_-5px_rgba(230,169,91,0.7)] transition">
                      <PlayCircle className="w-8 h-8" />
                    </div>
                  </div>
                  {item.title && (
                    <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-[#E6A95B] mb-1">{t("cats.Behind the Scenes")}</div>
                      <div className="font-display text-sm md:text-base text-white line-clamp-1">{item.title}</div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <VideoModal
        open={!!activeBtsVideo}
        onClose={() => setActiveBtsVideo(null)}
        videoUrl={activeBtsVideo}
        title={t("cats.Behind the Scenes")}
      />
    </div>
  );
};

export default Work;
